import { createContext, useState } from 'react'

import request from 'api'
import { useLoadScript } from 'hooks/use-load-script'
import { debugLog } from 'utils/common'

type RecaptchaContextType = {
  instance: any
  siteKey: string
  setId: Function
  execute: Function
  executePromise: Function
  assessment: Function
  assessmentPassword: Function
  annotate: Function
}

const RecaptchaContext = createContext<RecaptchaContextType>({
  instance: undefined,
  siteKey: '',
  setId: (account: string) => {},
  execute: (action: string, success: Function, failure: Function) => {},
  executePromise: (action: string) => {},
  assessment: async (token: string, action: string) => {},
  assessmentPassword: () => {},
  annotate: () => {}
})

type RecaptchaProviderProps = {
  children: React.ReactNode
}

const RecaptchaProvider = ({ children }: RecaptchaProviderProps) => {
  const [instance, setInstance] = useState<any>(undefined)
  const [username, setUserName] = useState<string>('')

  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true'
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''

  useLoadScript({
    enabled: recaptchaEnabled,
    id: 'google-recaptcha-script',
    src: `https://www.recaptcha.net/recaptcha/enterprise.js?render=${recaptchaSiteKey}`,
    onLoad: () => {
      if (!window.grecaptcha?.enterprise) {
        console.error('Failed to load recaptcha script')
      } else {
        window.grecaptcha.enterprise.ready(() => {
          setInstance(window.grecaptcha.enterprise)
        })
      }
    }
  })

  const shouldNotExecute = () => {
    if (!recaptchaEnabled) {
      debugLog('recaptcha is not enabled')
      return true
    } else if (!window.grecaptcha?.enterprise || window.grecaptcha?.enterprise?.execute === undefined) {
      debugLog('recaptcha is not ready', window.grecaptcha?.enterprise)
      return true
    }

    return false
  }

  const execute = (action, success, failure) => {
    if (shouldNotExecute()) return

    window.grecaptcha?.enterprise
      .execute(recaptchaSiteKey, { action: action })
      .then((token) => {
        if (success !== undefined) {
          success(token)
        }
      })
      .catch(() => {
        if (failure !== undefined) {
          failure()
        }
        console.error('execute failure')
      })
  }

  const executePromise = async (action: string) => {
    if (shouldNotExecute()) return

    return new Promise((resolve, reject) => {
      execute(
        action,
        (token) => {
          resolve(token)
        },
        () => {
          reject('failed to recaptcha execute')
        }
      )
    })
  }

  const setId = (account: string) => {
    setUserName(account)
  }

  const assessment = async (action: string) => {
    if (shouldNotExecute()) return

    let token
    try {
      token = await executePromise(action)
    } catch (error) {
      return undefined
    }

    const params = {
      token: token,
      action: action
    }

    if (username !== '') {
      params['account'] = username
    }

    const configs = {
      headers: { 'Content-Type': 'application/json' }
    }

    let response
    try {
      response = await request.post('/v1/recaptcha/assessments', params, configs)
    } catch (error) {
      console.error('assessment failure : ' + error)
      return undefined
    }

    return response.data.assessmentId
  }

  const assessmentPassword = async (action: string, password: string) => {
    if (shouldNotExecute()) return

    if (username === undefined || password === undefined) {
      return
    }

    if (username === '') {
      return
    }

    let token
    try {
      token = await executePromise(action)
    } catch (error) {
      return undefined
    }

    const params = {
      token: token,
      action: action,
      account: username,
      password: password
    }

    const configs = {
      headers: { 'Content-Type': 'application/json' }
    }

    try {
      await request.post('/v1/recaptcha/assessments', params, configs)
    } catch (error) {
      console.error('assessment failure : ' + error)
      return
    }
  }

  const annotate = async (assessmentId: string, annotation: string, reason: string) => {
    if (shouldNotExecute()) return

    const params = {
      assessmentId: assessmentId,
      account: username,
      annotation: annotation,
      reason: reason
    }

    const configs = {
      headers: { 'Content-Type': 'application/json' }
    }

    try {
      await request.post('/v1/recaptcha/assessments/annotations', params, configs)
    } catch (error) {
      console.error('annotate failure : ' + error)
      return
    }
  }

  return (
    <RecaptchaContext.Provider
      value={{
        instance,
        siteKey: recaptchaSiteKey,
        setId,
        execute,
        executePromise,
        assessment,
        assessmentPassword,
        annotate
      }}
    >
      {children}
    </RecaptchaContext.Provider>
  )
}

export { RecaptchaContext, RecaptchaProvider }
